import axiosClient from "../axios";
import {playSound} from "@/notificationSound";

/* Notifications are delivered by polling the API every 10 seconds. The timer
   handle lives at module scope so the polling actions stay idempotent no matter
   how many times AppLayout mounts. */
const NOTIFICATION_POLL_INTERVAL = 10000;
let notificationPollTimer = null;
let markingNotificationsAsRead = false;

export function getReviews({commit}, {url = null, search = null, perPage = 50, status, rate} = {}) {
    url = url || "/reviews";
    return axiosClient
        .get(url, {
            params: {search, perPage, status, rate},
        })
        .then(({data}) => {
            commit("setReviews", data);
            return data;
        })
        .catch(() => {
        });
}

export function createReview(__, review) {
    if (Array.isArray(review.images)) {
        const formData = new FormData();
        formData.append('full_name', review.full_name);
        formData.append('title', review.title);
        formData.append('description', review.description);
        formData.append('rate', review.rate);
        review.images.forEach((file, index) => {
            formData.append(`images[${index}]`, file); // Add each file to FormData
        });
        review = formData;
    }
    return axiosClient.post("/reviews", review);
}


export function updateReview(__, review) {
    const id = review.id;

    review._method = "PUT";

    return axiosClient.post(`/reviews/${id}`, review);
}

export function deleteReview(__, id) {
    return axiosClient.delete(`/reviews/${id}`);
}

export function getReview(__, id) {
    return axiosClient.get(`/reviews/${id}`);
}

export function toggleReviewActive(__, id) {
    return axiosClient.post(`/reviews/${id}/toggle-active`)
}

/*End Of Crud*/
export function login({commit}, data) {
    return axiosClient.post("/login", data).then(({data}) => {
        commit("setUser", data.user);
        commit("setToken", data.token);
        return data;
    });
}

export function logout({commit, dispatch}) {
    // Stop polling up front so a failed logout request cannot leave the timer running.
    dispatch("stopNotificationPolling");
    return axiosClient.post("/logout").then(() => {
        commit("setUser", []);
        commit("setToken", null);
    });
}

export function getCurrentUser({commit}) {
    return axiosClient.get("/user").then(({data}) => {
        commit("setUser", data);
    });
}

export function getDashboard({commit}) {
    commit("setDashboardLoading", true);
    return axiosClient.get("/dashboard/stats")
        .then(({data}) => {
            commit("setDashboard", data);
            return data;
        })
        .catch((error) => {
            commit("setDashboardLoading", false);
            throw error;
        });
}

/*End Of Dashboard */

export function getNotifications({commit}) {
    commit("setNotificationsLoading", true);
    return axiosClient.get("notifications").then(({data}) => {
        commit("setNotifications", [data, false]);
    });
}

export function startNotificationPolling({dispatch}) {
    dispatch("stopNotificationPolling");
    notificationPollTimer = setInterval(
        () => dispatch("pollNotifications"),
        NOTIFICATION_POLL_INTERVAL
    );
}

export function stopNotificationPolling() {
    if (notificationPollTimer) {
        clearInterval(notificationPollTimer);
    }
    notificationPollTimer = null;
}

export function pollNotifications({commit, state}) {
    // Skip while a mark-as-read is in flight, otherwise a poll landing mid-request
    // would briefly restore the badge and replay the sound.
    if (markingNotificationsAsRead) {
        return Promise.resolve();
    }
    const previousUnread = state.UnReadNotificationCount;
    return axiosClient.get("notifications")
        .then(({data}) => {
            commit("setNotifications", [data, false]);
            if (state.UnReadNotificationCount > previousUnread) {
                playSound();
            }
        })
        .catch(() => {
        });
}

export function markNotificationAsRead({dispatch}) {
    markingNotificationsAsRead = true;
    return axiosClient.post("mark-notification-as-read")
        .finally(() => {
            markingNotificationsAsRead = false;
            // Re-sync so the badge reflects the server rather than the optimistic reset.
            return dispatch("pollNotifications");
        });
}

export function getVideos({commit}) {
    return axiosClient.get('/youtube-videos').then(({data}) => {
        commit('setVideos', data)
    })
}

export function getVideo(__, id) {
    return axiosClient.get(`/youtube-videos/${id}`)
}

export function createVideo(__, video) {
    return axiosClient.post('/youtube-videos', video)
}

export function updateVideo(__, video) {
    const id = video.id;
    return axiosClient.put(`/youtube-videos/${id}`, video);
}

export function deleteVideo(__, id) {
    return axiosClient.delete(`/youtube-videos/${id}`)
}

export function toggleReplyActive(__, id) {
    return axiosClient.delete(`/toggle-reply-active/${id}`)
}

export function getActiveReviews({commit}, {url = null, perPage = 50} = {}) {
    url = url || "/active-reviews";
    return axiosClient
        .get(url, {
            params: {perPage},
        })
        .then(({data}) => {
            commit("setActiveReviews", data);
            return data;
        })
        .catch(() => {
        });
}

export function addReply(__, reply) {
    if (reply.url instanceof File) {
        const formData = new FormData();
        formData.append('full_name', reply.full_name);
        formData.append('message', reply.message);
        formData.append('url', reply.url);
        formData.append('review_id', reply.review_id);
        reply = formData;
    }
    return axiosClient.post(`/replies`, reply)
}

export function addAdminReply(__, reply) {

    if (Array.isArray(reply.url)) {
        const formData = new FormData();
        formData.append('full_name', reply.full_name);
        formData.append('message', reply.message);
        reply.url.forEach((file, index) => {
            formData.append(`images[${index}]`, file); // Add each file to FormData
        });
        formData.append('review_id', reply.review_id);
        reply = formData;
    }
    return axiosClient.post(`/admin/add-reply`, reply)
}

export function deleteReply(__, id) {
    return axiosClient.delete(`admin/replies/${id}`)
}

export function updateReply(__, reply) {
    const id = reply.id;
    reply._method = 'PUT';
    return axiosClient.post(`/admin/replies/${id}`, reply)
}

export function getGalleries({commit}, {url = null, perPage = 10} = {}) {
    url = url || "/galleries";
    return axiosClient
        .get(url, {params: {perPage}})
        .then(({data}) => {
            commit("setGalleries", data);
            return data;
        });
}

export function getGallery(__, id) {
    return axiosClient.get(`/galleries/${id}`).then(({data}) => {
        return data;
    })
}

export function createGallery(__, gallery) {
    const formData = new FormData();
    formData.append('url', gallery.url);
    gallery = formData;
    return axiosClient.post('/galleries', gallery).then(({data}) => {
        return data;
    })
}

export function updateGallery(__, gallery) {
    const id = gallery.id;
    const formData = new FormData();
    formData.append('url', gallery.url);
    formData.append('_method', 'PUT');
    gallery = formData;
    return axiosClient.post(`/galleries/${id}`, gallery).then(({data}) => {
        return data;
    })
}

export function deleteGallery(__, id) {
    return axiosClient.delete(`/galleries/${id}`).then(({data}) => {
        return data;
    })
}

export function getUsersImages({commit}) {
    return axiosClient.get('/users-images').then(({data}) => {
        commit('setUsersImages', data)
        return data;
    })
}
